import React from "react";
import { Typography } from "@/components/ui/typography";
import DashboardCardWrapper from "../common/DashboardCardWrapper";
import { CardContent } from "../ui/card";
import DashboardCardHeader from "../common/DashboardCardHeader";
import CareIcon from "@/assets/icons/caremanagement.svg";
import { CardSkeleton } from "@/components/ui/skeleton";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { routes } from "@/utils/urls";
import BorderedIconWrapper from "../common/BorderIconWrapper";
import { TruncatedTextTooltip } from "@/components/common/TextTooltip";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { useFetchCard } from "@/hooks/useFetchCard";
import {
  formatDateWithoutTimezone,
  formatPhoneNumber,
} from "@/constants/utilityFunctions";

interface CareManagementData {
  [key: string]: string;
}

const handleNullValues = (
  data: CareManagementData | null
): CareManagementData | null => {
  if (!data || typeof data !== "object") return data;

  const processed: CareManagementData = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || value === "") {
      processed[key] = "-";
    } else {
      processed[key] = value;
    }
  }
  return processed;
};

interface CareManagementCardProps {
  shouldBlink?: boolean;
}

const CareManagementCard: React.FC<CareManagementCardProps> = ({
  shouldBlink = false,
}) => {
  const { cardVariant, isFavourite, toggleFavourite } =
    useDashboardCardFavorite("CARE_MANAGEMENT");

  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const {
    data: careManagement,
    loading,
    error,
    isErrorRed,
  } = useFetchCard<CareManagementData>({
    endpoint: routes.cards.caremanagemnt,
    memberId,
    processData: (rawData: unknown) =>
      handleNullValues((rawData as { data: CareManagementData }).data),
  });

  const CARE_MANAGEMENT_LABELS: Record<string, string> = {
    agencyName: "Agency Name",
    agencyPhone: "Phone Number",
    agencyAddress: "Agency Address",
    agencyCity: "City",
    agencyState: "State",
    careManager: "Care Manager",
    assessmentCompletionDate: "Comprehensive Assessment Completion Date",
    carePlanCompletionDate: "Care Plan Completion Date",
    lastSuccessContactDate: "Last Successful Contact Date",
    status1915i: "1915i Status",
    status1915iDate: "1915i Status Date",
    consentStatus: "Consent Status",
    consentStatusExpiryDate: "Consent Status Expiry Date",
  };

  const orderedKeys = [
    "agencyName",
    "agencyPhone",
    "agencyAddress",
    "agencyCity",
    "agencyState",
    "careManager",
    "assessmentCompletionDate",
    "carePlanCompletionDate",
    "lastSuccessContactDate",
    "status1915i",
    "status1915iDate",
    "consentStatus",
    "consentStatusExpiryDate",
  ];

  const entries = careManagement
    ? orderedKeys.map((key) => [key, careManagement[key]])
    : [];

  // Field groups: Agency Info, Care Team/Clinical Milestones, 1915i, Consent —
  // a divider is rendered after the last key in each group.
  const DIVIDER_AFTER_KEYS = new Set([
    "agencyState",
    "lastSuccessContactDate",
    "status1915iDate",
  ]);

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
      <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
        <DashboardCardHeader
          icon={
            <BorderedIconWrapper>
              <img src={CareIcon} alt='CareManagement' className='w-4 h-4' />
            </BorderedIconWrapper>
          }
          title='Care Management'
          className='h-[56px]'
          showStar={true}
          isFavourite={isFavourite}
          onToggleFavourite={toggleFavourite}
        />

        <CardContent className="pt-[16px] lg:h-[590px] h-max-content">
          {(() => {
            if (loading) {
              return <CardSkeleton rows={17} showProfile={false} />;
            }

            if (error) {
              return (
                <div className='flex items-center justify-center h-full lg:h-[523px] md:h-[523px]'>
                  <Typography
                    variant='medium-14'
                    className={`${isErrorRed
                      ? "text-red-500 dark:text-red-400"
                      : "text-grey-600 dark:text-grey-500"
                      } font-medium`}>
                    {error}
                  </Typography>
                </div>
              );
            }

            if (!careManagement) {
              return null;
            }

            return (
              <div className='gap-[16px] flex flex-col'>
              {entries.map(([key, value], index) => {
                const label = CARE_MANAGEMENT_LABELS[key] || key;

                let formattedValue = value;
                if (key === "agencyPhone") {
                  formattedValue = formatPhoneNumber(value);
                } else if (key.toLowerCase().includes("date")) {
                  formattedValue = formatDateWithoutTimezone(value);
                }

                const isLastItem = index === entries.length - 1;
                const showDivider = DIVIDER_AFTER_KEYS.has(key) && !isLastItem;

                return (
                  <React.Fragment key={key}>
                    <div className='flex justify-between items-center gap-2'>
                      {/* Label with truncation on all screens */}
                      <div className='flex-shrink min-w-0 max-w-[55%] md:max-w-[65%]'>
                        <TruncatedTextTooltip text={label}>
                          <Typography
                            variant='medium-14'
                            className='text-grey-700 dark:text-grey-500 truncate whitespace-nowrap'>
                            {label}
                          </Typography>
                        </TruncatedTextTooltip>
                      </div>

                      {/* Value */}
                      <div className='flex-shrink-0 max-w-[45%] md:max-w-[35%] text-right'>
                        {formattedValue === "Active" ||
                          formattedValue === "Inactive" ? (
                          <Typography
                            variant='medium-14'
                            className='text-gray-800 dark:text-white'>
                            {formattedValue}
                          </Typography>
                        ) : (
                          <TruncatedTextTooltip text={formattedValue}>
                            <Typography
                              variant='medium-14'
                              className='text-gray-800 dark:text-white truncate whitespace-nowrap'>
                              {formattedValue}
                            </Typography>
                          </TruncatedTextTooltip>
                        )}
                      </div>
                    </div>

                    {showDivider && (
                      <div className='border-b border-dashed border-gray-300 dark:border-gray-600 mb-2'></div>
                    )}
                  </React.Fragment>
                );
              })}
              </div>
            );
          })()}
        </CardContent>
      </DashboardCardWrapper>
    </div>
  );
};

export default CareManagementCard;
